export default `# Styling the Movies

Let's have a look at the application mockup again:

![Sweet Pumpkins app mockup](https://sweetpumpkins.codecamps.com/sw-mockup.png)

In this chapter, we are going to style the main area of the application.

We have to allocate space to both \`Navigation\` and \`Movies\` components first.
We want to give the \`Navigation\` component a \`min-width: 300px;\`. The \`Movies\` component should take up the rest.

Before we start writing CSS, let's refresh our knowledge about space allocation.

In the example below, when we set the \`display: flex;\` on the \`.main\` class, the \`div\` element becomes a flex container.
It's children with classes: \`.navigation\` and \`movies\` become flex items.

To set the width of flex items, we use the \`flex-basis\`. And to set the minimum width of the flex item, we
will use the \`min-width\`. Let's look at the example:

\`\`\`html
<div class="main">
  <div class="navigation"></div>
  <div class="movies"></div>
</div>
\`\`\`

\`\`\`css
.main {
  display: flex;
}

.navigation, .movies {
  height: 200px;
}

.navigation {
  flex-basis: 20%;
  min-width: 300px;
  background: tomato;
}

.movies {
  flex-basis: 80%;
  background: royalblue;
}
\`\`\`

If you want to learn more about how the \`flex-basis\` and other superpowers of the flex-box model work, you can read my
[article](http://www.codecamps.com/3-superpowers-of-the-flex-box-model/) that explains it in detail.

Let's divide the space for our components:
\`\`\`plainCss
/* Main.css*/

.main {
  display: flex;
}
\`\`\`

\`\`\`plainCss
/* Navigation.css*/

.navigation {
  flex-basis: 20%;
  min-width: 300px;
  padding: 20px;
}
\`\`\`

\`\`\`plainCss
/* Movies.css*/

.movies {
  flex-basis: 80%;
  padding: 20px 0;
  margin: 0;
}
\`\`\`

Now, the \`Navigation\` and \`Movies\` containers share the space nicely.

But we have to fix how the movies are displayed. At the moment, they are stuck on top of each other. Let's fix it by
changing the \`Movies\` into a flex container:

\`\`\`plainCss
/* Movies.css*/

.movies {
  flex-basis: 80%;
  display: flex;
  flex-wrap: wrap;

  padding: 20px 0;
  margin: 0;
}
\`\`\`

But what is the \`flex-wrap: wrap;\`?

When you change the \`Movies\` into a flex container by setting \`display: flex;\` on the \`.movies\` class,
all movies will be displayed in one long row, overflowing the \`Movies\` container.

And we want the \`MovieListItems\` to flow like words in a book. When they can't fit on the same line, they should wrap to the next line.

Let's have a look at the example below:

\`\`\`html
<div class="movies">
  <div class="movie"></div>
  <div class="movie"></div>
  <div class="movie"></div>
  <div class="movie"></div>
  <div class="movie"></div>
  <div class="movie"></div>
</div>
\`\`\`

\`\`\`css
.movies {
  display: flex;
  flex-wrap: wrap;
  background: smokewhite;
}

.movie {
  width: 240px;
  height: 100px;
  margin: 5px;
  background: tomato;
}
\`\`\`

Adding the \`flex-wrap: wrap;\` to the container does exactly what we need.
If movies don't fit in a line, they are just displayed in the next one.

Finally, we have to take care of our \`MovieListItem\`. First, let's get rid of list bullets and set its display property to \`flex\`,
so that later we will be able to arrange its content more easily:

\`\`\`plainCss
/* MovieListItem.css */

.movie-item {
  flex-basis: 22%;
  box-sizing: border-box;
  margin: 1.5%;
  display: flex;
  flex-direction: column;
  list-style: none;
}

.movie-item img {
  width: 100%;
}
\`\`\`

Above, we also set the width to \`22%\`, to display 4 movies in one line.
We also set the \`img\` width to \`100%\` – otherwise it would be displayed at its original size.
Now, movies should align nicely.

But we are getting more information from the TMDB API than just the movie title.
Let's also display the rating and the year of production.

Here is a mockup for the final version of the \`MovieListItem\`:

![](https://sweetpumpkins.codecamps.com/movie-list-item-min.jpg)

We want our \`movie\` to have an image and \`movie-description\` area.
The movie description area should display a title of the movie and below another \`movie-details\` section.
The \`movie-details\` section should have inside two elements: \`movie-year\` and \`movie-rating\`.

The HTML and CSS implementation may look like below. Can you move it to respective files? Place the styles in *MovieListItem.css*.
When copying and pasting, keep in mind that you have to replace \`class\` with \`className\`.

\`\`\`html
<ul>
  <li class="movie-item">
    <img src="https://image.tmdb.org/t/p/w342/9E2y5Q7WlCVNEhP5GiVTjhEhx1o.jpg" alt="" />
    <div class="movie-description">
      <h2>It</h2>
      <section class="movie-details">
        <div class="movie-year">
          <span class="title">Year</span>
          <span>2015</span>
        </div>
        <div class="movie-rating">
          <span class="title">Rating</span>
          <span>7.5</span>
        </div>
      </section>
    </div>
  </li>
</ul>
\`\`\`

\`\`\`css
ul {
  display: flex;
  padding: 20px;
}
.movie-item {
  flex-basis: 22%;
  display: flex;
  flex-direction: column;
  list-style: none;
  box-sizing: border-box;
  margin: 1.5%;
  border: 1px solid #eee;
  box-shadow: 0 10px 28px -7px rgba(0,0,0,0.1);
}

.movie-item img {
  width: 100%;
}

.movie-description {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: space-between;
  padding: 10px;
}

.movie-description h2 {
  color: #555;
  font-weight: bold;
  margin-bottom: 20px;
}

.movie-details {
  display: flex;
  justify-content: space-between;
}

.movie-details span {
  color: #555;
  font-size: 0.8rem;
  font-weight: bold;
}

.movie-year, .movie-rating {
  display: flex;
  flex-direction: column;
}

.movie-year .title, .movie-rating .title {
  color: #aaa;
  margin-bottom: 5px;
  font-size: 0.65rem;
  font-weight: normal;
}

.movie-rating {
  align-items: flex-end;
}
\`\`\`

Here is the short explanation:
- All the flex items (movies) stretch horizontally by default to fill the same space as the highest element in the row
- Because some elements have a long, two- or three-line titles, we need to stretch the \`.movie-description\` element so it takes the whole vertical space. That's why we set the \`flex-grow: 1;\`
- To push the movie title and \`.movie-details\` apart along the main-axis (it's vertical because of \`flex-direction: column;\`), we use \`justify-content: space-between;\`

Here is the visual explanation:
![spacing the .movie-desciption](https://sweetpumpkins.codecamps.com/spacing-movie-description-min.jpg)

Finally, we can replace the fixed year of production (2015) and rating in the \`MovieListItem\` with actual values.
The \`movie\` object comes with a \`release_date\` property in format \`YYYY-MM-DD\`. And we need only first 4 characters.

\`\`\`plainJsx
// MovieListItem.js

import React from "react";
import "./MovieListItem.css";

const MovieListItem = ({ movie }) => {
  const { title, poster_path, release_date, vote_average } = movie;
  const imgUrl = \`https://image.tmdb.org/t/p/w342/\${poster_path}\`;
  const year = release_year.substring(0, 4);

  return (
    <li className="movie-item">
      <img src={imgUrl} alt={title} />
      <div className="movie-description">
        <h2>{title}</h2>
        <section className="movie-details">
          <div className="movie-year">
            <span className="title">Year</span>
            <span>{year}</span>
          </div>
          <div className="movie-rating">
            <span className="title">Rating</span>
            <span>{vote_average}</span>
          </div>
        </section>
      </div>
    </li>
  );
};

export default MovieListItem;
\`\`\`

And our final code.
Click the orange tab below for a better preview or click an arrow next to the address bar to preview the result in a separate browser tab.

\`\`\`codeSandbox
0p0wmn9280
\`\`\`

`;



// WEBPACK FOOTER //
// ./src/book/chapters/app-movies-styles.js